// The Frontier's island as a list of things to draw, in the same world units
// and with the same Kenney models the table uses. Pure, so it can be
// unit-tested without a GPU: `layoutFrontier(civ, { targets })` -> tiles and
// features. frontierWorld.js draws it; FrontierScene.vue hangs the labels.
//
// Matt, 2026-09-19: "This map isn't particularly exciting. It would be way
// cooler if we could do this in three dimensions like we did for the town.
// And I can move around on the actual map."
//
// The difference from the table's layoutWorld: this one is the FRONTIER's
// map. It knows what has been seen and what has not, where the SELECTED
// unit could step this turn, and every camp's strength - none of which the
// table shows.

import {
  key, siteAt, baseTerrain, isTown, isWild, neighbours, allSites, hash, within,
} from '@/engine/map';
import { sitesOf, wondersOf } from '@/engine/cards';
import { FINDS, stepCost, workAt } from '@/engine/terrain';
import { canFoundAt } from '@/engine/roleActions';
import { rivalGround, rivalsOf, rivalColour } from '@/engine/rivals';
import {
  REGION_LOOKS, TILE_TOP, hexToWorld, MENACE_MODELS, OUTPOST_MODEL, BAND_MODEL, FLAT_UNDER, townFeatures, TOWN_PARTS, roadPaths,
} from './layout';
import {
  pieceSpots, LANDMARK, SIDE_COLOURS, ROUGH, FLAT_OF, partyHexes, flushY,
} from './mapPieces';

export { hexToWorld };

// Unseen ground is not a model: it is a dark slab, drawn by the renderer.
// The island beyond it is not drawn at all, so the shape of what you have
// walked is the shape of the board.
export const FOG_MODEL = 'fog';
export const TOWN_MODEL = 'building-village';
export const GREAT_WORK_MODEL = 'building-castle';
// Another people's seat, and the marks on the ground they hold.
// A watchtower from the tower-defense kit: another people's seat should not
// look like one of our outposts.
// The TV's town, not a box of sticks (playtest 11, Matt: "the town with the flag is correct").
export const RIVAL_SEAT_MODEL = 'building-village';
export const RIVAL_MARK_MODEL = 'unit-house';

const place = (id, model, q, r, y, rot = 0, dx = 0, dz = 0, scale = 1) => ({
  id, model, q, r, y, rot, dx, dz, scale,
});

/** Every model this board can ask for, so the renderer can preload the lot. */
export const MODELS_FOR_FRONTIER = [...new Set([
  'grass', 'grass-forest', 'grass-hill', 'dirt', 'sand', 'sand-rocks', 'stone',
  'stone-hill', 'stone-mountain', 'stone-rocks', 'water', 'water-rocks', 'water-island',
  TOWN_MODEL, GREAT_WORK_MODEL, OUTPOST_MODEL, RIVAL_SEAT_MODEL, RIVAL_MARK_MODEL,
  // The capital as the TV draws it: Kenney town and castle kit parts (`town:` recipes).
  ...TOWN_PARTS,
  ...Object.values(MENACE_MODELS),
  ...Object.values(FINDS).map((f) => f.model),
  ...Object.values(REGION_LOOKS).flatMap((s) => [...s.terrain, ...s.features.filter(Boolean)]),
])];

/**
 * @param {object} civ the commonwealth as the engine keeps it
 * @param {object} opts `targets` - [q, r] pairs the SELECTED unit could step
 *   to now; `selected` - which unit is in hand; `ease` - roads
 * @returns {{ tiles: object[], features: object[] }}
 */
export const layoutFrontier = (civ, { targets = [], selected = null, ease = 0, extra = 0 } = {}) => {
  const seen = civ.seen ? new Set(civ.seen) : null;
  const isSeen = (q, r) => !seen || seen.has(key(q, r));
  const found = new Set(civ.regions || []);
  const sites = sitesOf(civ);
  const targetSet = new Set(targets.map(([q, r]) => key(q, r)));
  const units = civ.units || [];
  const chosen = units.find((u) => u.id === selected) || null;

  const tiles = [];
  const features = [];
  const modelAt = new Map();
  // Which camp, if any, is standing on each hex: the hex is the tap target
  // for it, so a thumb has something the size of a hex to aim at.
  const menaceAt = new Map();
  (civ.menaces || []).forEach((m) => menaceAt.set(key(m.q, m.r), m.id));
  // Raiders are pieces on the map too, and they are also tap targets: the
  // hex is what a thumb aims at.
  const raiderAt = new Map();
  (civ.raiders || []).forEach((x) => raiderAt.set(key(x.q, x.r), x.id));
  const villageAt = new Map();
  (civ.villages || []).forEach((v) => villageAt.set(key(v.q, v.r), v));
  // Ground another people holds. Their seat is a tower; every other hex of
  // theirs carries a small house, so their border reads on the map.
  const rivalAt = rivalGround(civ);
  const cityHexes = new Set((civ.city?.hexes || []).map(([a, b]) => key(a, b)));
  const parties = partyHexes(civ);
  const spots = pieceSpots(civ);

  // The island AND the ring of sea around it, as the TV draws it (playtest
  // 11, Matt: a unit "reached the edge of the ocean... on the frontier map I
  // don't see the ocean, it just looks like darkness"; and the TV showed
  // more than this board did). Seen sea at ISLAND_RADIUS + 1 is water.
  // The world is generated as it is seen (engine/map.js): the seen, and one
  // ring of dark ground beyond it, so the map has an edge to walk towards.
  const seed = civ.mapSeed || 0;
  const near = new Set();
  (seen ? [...seen].map((k0) => k0.split(',').map(Number)) : within(0, 0, 9)).forEach(([a, b]) => {
    near.add(key(a, b));
    neighbours(a, b).forEach(([c, d]) => near.add(key(c, d)));
  });
  for (const k of near) {
    const [q, r] = k.split(',').map(Number);
    {
      const here = isSeen(q, r);

      const site = siteAt(q, r, civ);
      const regionId = site >= 0 ? sites[site] : null;
      const risen = Boolean(regionId) && found.has(regionId);
      let model = 'water';
      if (here) {
        if (site >= 0) {
          // A unit sees the ground beside it, region or not: an unfound
          // region's hex used to stay black right next to a warband
          // (playtest 9bkJg). Its ground shows; what stands on it waits
          // until someone walks on and finds it.
          const look = REGION_LOOKS[regionId];
          const i = allSites(civ)[site].findIndex(([a, b]) => a === q && b === r);
          model = look?.terrain[i] || 'grass';
          const feat = risen && look?.features[i];
          // Small, at the back of the hex: never on the middle, where a unit
          // stands (playtest 12, Brian: "those extra buildings and stuff
          // cover up the units"). The TV draws it the same (mapPieces.js).
          if (feat) features.push(place(`region:${regionId}:${i}`, feat, q, r, flushY(TILE_TOP[model] ?? 0.2, feat, LANDMARK.scale), hash(q, r, 9) * Math.PI * 2, LANDMARK.dx, LANDMARK.dz, LANDMARK.scale));
        } else {
          model = baseTerrain(q, r, seed) || 'water';
        }
      }
      if (here && villageAt.has(k)) model = FLAT_UNDER[model] || model;
      // A party never stands inside a forest or a mountain: flat ground,
      // and the trees or the peak small at the corner (mapPieces ROUGH).
      if (here && parties.has(k) && FLAT_OF[model]) {
        features.push(place(`rough:${k}`, model, q, r, (TILE_TOP[FLAT_OF[model]] ?? 0.2) - 0.2 * ROUGH.scale, hash(q, r, 9) * Math.PI * 2, ROUGH.dx, ROUGH.dz, ROUGH.scale));
        model = FLAT_OF[model];
      }
      if (here) modelAt.set(k, model);
      tiles.push({
        id: `t${k}`,
        // Unknown ground is dark, never sea - the TV's rule (2026-09-25):
        // an unfound region used to look like ocean until walked onto.
        model: here ? model : FOG_MODEL,
        q,
        r,
        y: 0,
        fog: !here,
        // The hexes a march could reach this turn. Matt: "It'd be nice if I
        // could see how much I can move around."
        target: targetSet.has(k),
        here: Boolean(chosen) && q === chosen.q && r === chosen.r,
        town: here && isTown(q, r),
        // The city's own hexes wear a band, so the town has an EDGE on this
        // map (playtest 6: "the boundaries of the town need to be more
        // clearly marked").
        city: here && cityHexes.has(k),
        wild: here && isWild(q, r, civ),
        // Unknown ground: walk onto it and whatever was dealt there rises.
        site: here && site >= 0 && !risen,
        menace: here ? (menaceAt.get(k) || null) : null,
        raider: here ? (raiderAt.get(k) || null) : null,
        village: here && villageAt.has(k) ? villageAt.get(k).id : null,
        rival: here ? (rivalAt.get(k) || null) : null,
        mine: here && (villageAt.has(k) || isTown(q, r)),
        // What a day's work here brings back, for the optional overlay
        // (playtest 11, Matt: "see what these places make by toggling an
        // overlay... instead of having to click and hold on each one").
        makes: here && !cityHexes.has(k) ? (workAt(civ, q, r)?.resource || null) : null,
        // ...and whether a village could be planted here at all, which is the
        // question the overlay is really for (Matt, after playtest 12: "you
        // have to really click around to see where to found your village").
        foundable: here && canFoundAt(civ, q, r),
        // What it costs to walk in. Rough ground is two, and the board puts
        // a chip on those so the price is on the map, not in a menu.
        step: stepCost(civ, q, r, ease) + extra,
      });
    }
  }

  const topOf = (q, r) => TILE_TOP[modelAt.get(key(q, r)) || 'grass'] ?? 0.2;

  // The town, so the place the band musters and stands down is a PLACE.
  if (isSeen(0, 0)) {
    const work = civ.monument || {};
    // The Great Wall is the town's own wall rising, not a castle in the square.
    const raised = (work.revealed && work.stages > 0 && civ.goal !== 'walls') || wondersOf(civ).some((w) => w !== 'walls');
    if (civ.city) {
      // THE SAME TOWN THE TV DRAWS (playtest 11, Matt: "they should really
      // match"): the city's hexes, its buildings and houses, the wall with
      // its gate and towers, the roads and the square - from one layout
      // (layout.js townFeatures), on the ground this board has seen.
      features.push(...townFeatures(civ, modelAt));
      if (raised) features.push(place('town', GREAT_WORK_MODEL, 0, 0, topOf(0, 0), 0, 0, 0, 0.3));
    } else {
      // An old save with no city: the one small village model, pushed away
      // from the camera so the people standing here are not buried.
      features.push(place('town', raised ? GREAT_WORK_MODEL : TOWN_MODEL, 0, 0, topOf(0, 0), 0, -0.14, -0.16, raised ? 0.55 : 0.6));
    }
  }

  for (const m of civ.menaces || []) {
    if (!isSeen(m.q, m.r)) continue;
    features.push(place(`menace:${m.id}`, MENACE_MODELS[m.kind] || MENACE_MODELS.camp, m.q, m.r, topOf(m.q, m.r), hash(m.q, m.r, 40) * Math.PI * 2, 0, 0, 0.7));
  }
  // Things worth walking to, once somebody has seen where they are.
  for (const f of civ.finds || []) {
    if (!isSeen(f.q, f.r)) continue;
    const what = FINDS[f.kind];
    if (!what) continue;
    features.push(place(`find:${f.id}`, what.model, f.q, f.r, topOf(f.q, f.r), hash(f.q, f.r, 55) * Math.PI * 2, 0, 0, 0.7));
  }
  // What the Frontier has planted. A chartered town gets the bigger model,
  // because a town you can muster at should look like somewhere.
  for (const v of civ.villages || []) {
    if (!isSeen(v.q, v.r)) continue;
    features.push({
      ...place(`village:${v.id}`, v.city ? GREAT_WORK_MODEL : (v.chartered ? TOWN_MODEL : OUTPOST_MODEL), v.q, v.r, flushY(topOf(v.q, v.r), v.city ? GREAT_WORK_MODEL : (v.chartered ? TOWN_MODEL : OUTPOST_MODEL), v.city ? 0.7 : (v.chartered ? 0.75 : 0.8)), 0, 0, 0, v.city ? 0.7 : (v.chartered ? 0.75 : 0.8)),
      damaged: Boolean(v.damaged),
      // A town another people holds wears their colour until it is won back.
      ...(v.taken ? { colour: rivalColour(v.taken), taken: v.taken } : {}),
    });
  }
  // The other peoples: a tower at the seat, a house on every hex they hold.
  for (const rv of rivalsOf(civ)) {
    (rv.held || []).forEach((k) => {
      const [q, r] = key === null ? [0, 0] : k.split(',').map(Number);
      if (!isSeen(q, r)) return;
      const seat = q === rv.q && r === rv.r;
      features.push({ ...place(`rival:${rv.id}:${seat ? 'seat' : k}`, seat ? RIVAL_SEAT_MODEL : RIVAL_MARK_MODEL, q, r, flushY(topOf(q, r), seat ? RIVAL_SEAT_MODEL : RIVAL_MARK_MODEL, seat ? 0.75 : 0.5), hash(q, r, 61) * Math.PI * 2, 0, 0, seat ? 0.75 : 0.5), colour: rivalColour(rv) });
      // Their land in their colour, as on the table (after playtest 9).
      features.push({ ...place(`rival:${rv.id}:claim:${k}`, 'claim', q, r, topOf(q, r) + 0.01, 0), colour: rivalColour(rv) });
    });
  }
  // The Frontier's forts (the Bronze Age), as the TV draws them.
  for (const k of civ.forts || []) {
    const [q, r] = k.split(',').map(Number);
    if (!isSeen(q, r)) continue;
    features.push(place(`fort:${k}`, 'unit-wall-tower', q, r, topOf(q, r), hash(q, r, 31) * Math.PI * 2, 0, 0, 0.8));
  }
  // What the camps have sent out, walking at something of yours.
  for (const x of civ.raiders || []) {
    if (!isSeen(x.q, x.r)) continue;
    const at = spots.get(`raider:${x.id}`) || { dx: 0, dz: 0, alone: true };
    const theirs = x.from ? rivalColour(rivalsOf(civ).find((rv) => rv.id === x.from) || {}) : null;
    features.push({
      ...place(`raider:${x.id}`, BAND_MODEL, x.q, x.r, topOf(x.q, x.r), 0, at.dx, at.dz),
      size: x.strength,
      foe: true,
      alone: at.alone,
      side: theirs || SIDE_COLOURS.raiders,
    });
  }
  // Your pieces. The strength travels with the placement: the renderer
  // stands one meeple per point, so you can count a unit without reading
  // its chip, and the SELECTED one is drawn ringed.
  for (const u of units) {
    // ALONE on its hex (no other unit, no settlement, not the city), a unit
    // stands in the MIDDLE with its people spread over the six spaces
    // (playtest 11, Matt: "if they're alone on a hex, stand in the center
    // instead of one little corner... each guy on one of the six spaces so
    // you could see the strength"). Sharing a hex, it is pushed clear of
    // whatever stands there - but only so far: pushed 0.36 with a huddle
    // 0.17 across on top, the people stood over the hex's edge (0.5 from the
    // middle) on the neighbour's ground (playtest, 2026-10-15, Brian: "almost
    // overlapping into the neighbouring hex"). 0.18 keeps all of them home.
    // Where it stands is decided once for both screens (mapPieces.js).
    const at = spots.get(`unit:${u.id}`) || { dx: 0, dz: 0, alone: true };
    features.push({
      ...place(`unit:${u.id}`, BAND_MODEL, u.q, u.r, topOf(u.q, u.r), 0, at.dx, at.dz),
      alone: at.alone,
      side: SIDE_COLOURS.ours,
      size: u.strength,
      kind: u.kind,
      chosen: u.id === selected,
    });
  }

  // Roads: the segments whose ends have both been seen (DESIGN.md "ROADS").
  const seenKeys = new Set(tiles.filter((t) => !t.fog).map((t) => `${t.q},${t.r}`));
  // The same road the TV draws, a lone road hex's stub included (layout.js roadPaths).
  const roads = roadPaths(civ).filter((sg) => seenKeys.has(`${sg.a.q},${sg.a.r}`) && (sg.half || seenKeys.has(`${sg.b.q},${sg.b.r}`)));
  return { tiles, features, roads };
};
